import { css } from '@emotion/react';
import { nonNull } from '@mythos/utils/utils';
import { useNavigate } from 'react-router-dom';

import { formatMonthYear } from '@mythos/utils/dateUtils';
import { match } from 'ts-pattern';
import { ColorPalette, UIColorPalette } from './ColorPalette';
import { graphql, useFragment } from './generated/gql';
import { UserSummaryFragment } from './generated/gql/graphql';
import { useGraphQLQuery, UserSummaryFragmentDoc } from './GraphQLHelpers';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';
import { useMobile } from './MobileContext';
import QueryStateRenderer from './QueryStateRenderer';

export const LeaderboardQuery = graphql(`
  query Leaderboard {
    leaderboard {
      edges {
        node {
          ...UserSummary
        }
      }
    }
  }
`);

export default function LeaderboardPage() {
  const state = useGraphQLQuery(LeaderboardQuery);

  return (
    <QueryStateRenderer state={state}>
      {(data) => {
        const users = data.leaderboard.edges
          .map((edge) => edge.node)
          .filter(nonNull);
        return (
          <div css={UserListStyles.container}>
            <h1 css={UserListStyles.title}>Leaderboard</h1>
            <ul css={UserListStyles.list}>
              {users.map((user, index) => {
                const userSummary = useFragment(UserSummaryFragmentDoc, user);
                return (
                  <LeaderboardEntry
                    key={userSummary.id}
                    user={userSummary}
                    index={index}
                  />
                );
              })}
            </ul>
          </div>
        );
      }}
    </QueryStateRenderer>
  );
}

function LeaderboardEntry({
  user,
  index,
}: {
  user: UserSummaryFragment;
  index: number;
}) {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const rank = index + 1;
  const rankText = match(rank)
    .with(1, () => '1st')
    .with(2, () => '2nd')
    .with(3, () => '3rd')
    .otherwise(() => `${rank}`);
  const joined = formatMonthYear(user.createdAt);
  return (
    <li
      key={user.id}
      css={UserListStyles.entry}
      onClick={() => {
        navigate(`/user/${user.id}`);
      }}
    >
      <div css={UserListStyles.rankBadge(rank)}>{rankText}</div>
      <img
        src={user.profilePictureURL}
        alt={user.name}
        css={UserListStyles.profilePicture}
      />
      <div css={UserListStyles.nameContainer}>
        <div css={UserListStyles.name}>{user.name}</div>
        <div css={UserListStyles.subtitle}>{user.subtitle}</div>
        <div css={UserListStyles.accountAge}>Joined {joined}</div>
      </div>
      <div css={UserListStyles.rightColumn}>
        <div css={UserListStyles.rating}>{user.stats?.rating ?? '-'}</div>
        <div css={UserListStyles.gamesWins}>
          <span>Games: {user.stats?.totalGames ?? 0}</span>
          {isMobile ? null : ' • '}
          <span>Wins: {user.stats?.totalWins ?? 0}</span>
        </div>
      </div>
    </li>
  );
}

const UserListStyles = {
  container: css({
    maxWidth: 600,
    margin: '24px auto',
    padding: '0 48px',
    [MEDIA_QUERY_MOBILE]: {
      padding: 0,
    },
  }),
  title: css({
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '2rem',
    textAlign: 'center',
  }),
  list: css({
    listStyle: 'none',
    padding: 8,
  }),
  entry: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderBottom: `2px solid ${UIColorPalette.listItemBottomBorder}`,
    backgroundColor: UIColorPalette.listBackground,
    gap: 8,
  }),
  rankBadge: (rank: number) =>
    css({
      minWidth: 40,
      textAlign: 'right',
      fontWeight: 800,
      fontSize: rank === 1 ? '1.5rem' : rank <= 3 ? '1.3rem' : '1.0rem',
      marginRight: 8,
      [MEDIA_QUERY_MOBILE]: {
        marginRight: 0,
      },
      color:
        rank === 1
          ? UIColorPalette.leaderboardGold
          : rank === 2
            ? UIColorPalette.leaderboardSilver
            : rank === 3
              ? UIColorPalette.leaderboardBronze
              : UIColorPalette.leaderboardPositionText,
    }),
  profilePicture: css({
    width: 50,
    height: 50,
  }),
  nameContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  }),
  name: css({
    fontSize: '1.3rem',
    fontWeight: 'bold',
    lineHeight: 1.2,
  }),
  subtitle: css({
    fontSize: '0.9rem',
    color: UIColorPalette.leaderboardSubtitleText,
    fontWeight: 400,
    marginTop: 2,
  }),
  accountAge: css({
    fontSize: '0.9rem',
    color: UIColorPalette.leaderboardAccountAgeText,
    marginTop: 2,
  }),
  rightColumn: css({
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
  }),
  rating: css({
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: UIColorPalette.ratingBadgeText,
    letterSpacing: '0.01em',
    padding: '4px 10px',
    borderRadius: 6,
    background: ColorPalette.darkPurpleBG,
    border: `1px solid ${UIColorPalette.ratingBadgeBorder}`,
    boxShadow: `0 1px 2px 0 ${UIColorPalette.ratingBadgeBoxShadow}`,
    display: 'inline-block',
  }),
  gamesWins: css({
    fontSize: '0.95rem',
    color: UIColorPalette.leaderboardGameWinsText,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    [MEDIA_QUERY_MOBILE]: {
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  }),
};
